import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { GlobalHeader } from '@/components/GlobalHeader';
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute';
import { useAuth, useCurrentUser } from '@/lib/auth/AuthProvider';
import { getHomeRoute } from '@/lib/auth/role-routing';

import { ChatPage } from './chat/ChatPage';
import { MapPage } from './map/MapPage';
import { GraphExplorer } from '@/studio/explorer/GraphExplorer';
import { ClientPortalHome } from './portal/ClientPortalHome';
import { AdminUsers } from './admin/AdminUsers';
import { LLMGovernance } from './admin/LLMGovernance';
import { NotFound } from '@/components/NotFound';

/**
 * Front-office shell — responsive ≥768px (Spec S01 §1.4).
 *
 * Mounted under `/app/*`. Auth is enforced once at the shell level via
 * `ProtectedRoute`; per-page role checks (e.g. admin) layer additional
 * `roles` constraints.
 */
export function AppShell() {
  const { isAuthenticated } = useAuth();
  const user = useCurrentUser();
  const navigate = useNavigate();

  // First successful authentication → redirect to the role-appropriate home
  // (only when the user landed on `/app` without a deeper path).
  useEffect(() => {
    if (isAuthenticated && user && window.location.pathname === '/app') {
      const target = getHomeRoute(user);
      if (target !== '/app') navigate(target, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen flex-col bg-white">
        <GlobalHeader />
        <main className="flex-1">
          <Routes>
            <Route index element={<Navigate to="chat" replace />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="chat/:threadId" element={<ChatPage />} />
            <Route path="map" element={<MapPage />} />
            <Route path="explorer" element={<GraphExplorer />} />
            <Route path="portal/home" element={<ClientPortalHome />} />
            <Route
              path="admin/users"
              element={
                <ProtectedRoute roles={['administrator']}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/llm"
              element={
                <ProtectedRoute
                  roles={['administrator', 'data_analyst_domain_expert']}
                >
                  <LLMGovernance />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </ProtectedRoute>
  );
}
